import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  ShieldAlert, 
  FileText, 
  ScanEye, 
  AlertTriangle,
  ChevronRight,
  Play,
  Loader2,
  Zap,
  TrendingUp,
  ArrowRight
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";

export const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_trademarks: 0,
    total_alerts: 0,
    new_alerts: 0,
    high_risk_alerts: 0,
    recent_alerts: []
  });
  const [statusChanges, setStatusChanges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanType, setScanType] = useState(null); // 'quick' or 'full'

  const fetchStats = async () => {
    try {
      const [statsRes, statusRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/status-changes/summary?days=30`)
      ]);
      setStats(statsRes.data);
      setStatusChanges(statusRes.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const runScan = async (quickScan = false) => {
    setScanning(true);
    setScanType(quickScan ? 'quick' : 'full');
    try {
      const response = await axios.post(`${API}/scan?quick_scan=${quickScan}`);
      toast.success(`Scan complete! Found ${response.data.new_alerts_count} potential conflicts`);
      fetchStats();
    } catch (error) {
      console.error("Error running scan:", error);
      toast.error("Scan failed. Please try again.");
    } finally {
      setScanning(false);
      setScanType(null);
    }
  };

  const getRiskBadgeClass = (level) => {
    switch (level) {
      case "high": return "badge-high-risk";
      case "medium": return "badge-medium-risk";
      case "low": return "badge-low-risk";
      default: return "badge-safe";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 tracking-tight">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">UK Trademark Monitoring Overview</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => runScan(true)}
            disabled={scanning}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="quick-scan-btn"
            title="Fast scan using basic similarity only"
          >
            {scanning && scanType === 'quick' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Zap className="w-4 h-4" />
            )}
            {scanning && scanType === 'quick' ? "SCANNING..." : "QUICK SCAN"}
          </Button>
          <Button
            onClick={() => runScan(false)}
            disabled={scanning}
            className="btn-primary flex items-center gap-2"
            data-testid="run-scan-btn"
            title="Full scan with AI-powered analysis"
          >
            {scanning && scanType === 'full' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {scanning && scanType === 'full' ? "SCANNING..." : "FULL SCAN"}
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          data-testid="stat-trademarks"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-slate-600" />
            </div>
            <span className="text-sm text-slate-500 uppercase tracking-wider">Trademarks</span>
          </div>
          <p className="stat-value text-slate-900">{stats.total_trademarks}</p>
          <p className="text-xs text-slate-400 mt-2">Being monitored</p>
        </motion.div>

        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          data-testid="stat-total-alerts"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-slate-600" />
            </div>
            <span className="text-sm text-slate-500 uppercase tracking-wider">Total Alerts</span>
          </div>
          <p className="stat-value text-slate-900">{stats.total_alerts}</p>
          <p className="text-xs text-slate-400 mt-2">All time</p>
        </motion.div>

        <motion.div 
          className={`swiss-card ${stats.new_alerts > 0 ? 'tracing-beam' : ''}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          data-testid="stat-new-alerts"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <span className="text-sm text-slate-500 uppercase tracking-wider">New Alerts</span>
          </div>
          <p className="stat-value text-orange-600">{stats.new_alerts}</p>
          <p className="text-xs text-slate-400 mt-2">Requires review</p>
        </motion.div>

        <motion.div 
          className={`swiss-card ${stats.high_risk_alerts > 0 ? 'tracing-beam' : ''}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          data-testid="stat-high-risk"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-sm text-slate-500 uppercase tracking-wider">High Risk</span>
          </div>
          <p className="stat-value text-red-600">{stats.high_risk_alerts}</p>
          <p className="text-xs text-slate-400 mt-2">Urgent attention</p>
        </motion.div>
      </div>

      {/* Status Changes Widget */}
      {statusChanges && (statusChanges.total_changes > 0 || statusChanges.watched_changes > 0) && (
        <motion.div 
          className="swiss-card mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          data-testid="status-changes-widget"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 flex items-center justify-center rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-heading font-bold text-slate-900">Status Changes</h2>
                <p className="text-sm text-slate-500">
                  {statusChanges.total_changes} changes in last 30 days
                  {statusChanges.watched_changes > 0 && (
                    <span className="text-amber-600 font-medium"> • {statusChanges.watched_changes} affecting your alerts</span>
                  )}
                </p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/status-changes')}
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 transition-colors"
              data-testid="view-status-changes-btn"
            >
              View All <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          {statusChanges.recent_changes.length > 0 && (
            <div className="space-y-2">
              {statusChanges.recent_changes.slice(0, 3).map((change) => (
                <div 
                  key={change.id}
                  className={`p-3 rounded-lg border ${change.is_watched ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate text-sm">{change.trademark_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600 uppercase">
                          {change.old_status}
                        </span>
                        <ArrowRight className="w-3 h-3 text-slate-400" />
                        <span className={`text-xs px-2 py-0.5 rounded uppercase ${
                          change.new_status === 'registered' ? 'bg-green-100 text-green-700' :
                          change.new_status === 'opposed' ? 'bg-purple-100 text-purple-700' :
                          change.new_status === 'dead' ? 'bg-slate-100 text-slate-500' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {change.new_status}
                        </span>
                      </div>
                    </div>
                    {change.is_watched && (
                      <span className="text-xs text-amber-600 font-medium ml-2">Watched</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Recent Alerts */}
      <div className="swiss-card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-bold text-slate-900">Recent Alerts</h2>
          <button 
            onClick={() => navigate('/alerts')}
            className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 transition-colors"
            data-testid="view-all-alerts-btn"
          >
            View All <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {stats.recent_alerts.length === 0 ? (
          <div className="text-center py-12" data-testid="no-alerts-message">
            <ScanEye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No alerts yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Add trademarks and run a scan to detect potential infringements
            </p>
          </div>
        ) : (
          <div className="space-y-3 stagger-children" data-testid="recent-alerts-list">
            {stats.recent_alerts.map((alert, index) => (
              <motion.div
                key={alert.id}
                className={`p-4 border cursor-pointer hover:bg-slate-50 transition-colors ${
                  alert.overall_risk_level === 'high' ? 'border-red-200 bg-red-50/50' :
                  alert.overall_risk_level === 'medium' ? 'border-orange-200 bg-orange-50/50' :
                  'border-slate-200'
                }`}
                onClick={() => navigate(`/alerts/${alert.id}`)}
                data-testid={`recent-alert-${alert.id}`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className={getRiskBadgeClass(alert.overall_risk_level)}>
                        {alert.overall_risk_level}
                      </span>
                      <span className="font-mono text-sm text-slate-400">
                        {Math.round(alert.basic_similarity_score * 100)}% match
                      </span>
                    </div>
                    <p className="font-semibold text-slate-900">{alert.conflicting_trademark_name}</p>
                    <p className="text-sm text-slate-500">
                      vs. <span className="font-medium">{alert.my_trademark_name}</span>
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
